import { useEffect, useMemo, useState } from 'react'
import {
  FaArrowLeft,
  FaBan,
  FaBullhorn,
  FaChartLine,
  FaDollarSign,
  FaCheckCircle,
  FaCompass,
  FaMapMarkerAlt,
  FaRoute,
  FaSatelliteDish,
  FaShieldAlt,
  FaSignOutAlt,
  FaUser,
  FaUserCheck,
  FaUsers
} from 'react-icons/fa'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'react-toastify'

import { useAuth } from 'contexts/auth.context'
import { useUser } from 'contexts/user.context'

import api from 'services/api'

import * as S from '../style'

type AdminUser = {
  id: string
  firstname: string
  lastname: string
  email: string
  country?: string
  profilePicture?: string
  createdAt: string
  status: 'active' | 'suspended' | 'banned'
  verified: boolean
  reason?: string | null
  role: 'master' | 'admin' | 'publisher' | 'commercial' | 'user'
  isAdmin: boolean
  isMaster?: boolean
  isPublisher?: boolean
  isCommercial?: boolean
  itineraries: number
  publishedItineraries: number
  followers: number
  lastActivity?: string | null
}

type UsersResponse = {
  items: AdminUser[]
  pagination: {
    page: number
    limit: number
    total: number
    pages: number
  }
  summary: {
    total: number
    active: number
    suspended: number
    banned: number
    verified: number
  }
}

type AdminMe = {
  isAdmin: boolean
  isMaster: boolean
  email: string
}

type UserDetails = AdminUser & {
  birthdate?: string
  gender?: string
  about?: string
  coverPicture?: string
  ratingsMade: number
  favorites: number
  following: number
  recentActivity: Array<{
    event_type: string
    entity_type?: string
    entity_id?: string
    value_text?: string
    created_at: string
  }>
}

const formatDate = (value?: string | null) => {
  if (!value) return 'Sem registro'
  return new Date(value).toLocaleDateString('pt-BR')
}

const formatDateTime = (value?: string | null) => {
  if (!value) return 'Sem atividade registrada'
  return new Date(value).toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  })
}

const statusLabel = (status: AdminUser['status']) => {
  if (status === 'suspended') return 'Suspenso'
  if (status === 'banned') return 'Bloqueado'
  return 'Ativo'
}

const eventLabel = (event: string) => {
  const labels: Record<string, string> = {
    page_view: 'Acessou uma página',
    search: 'Realizou uma busca',
    itinerary_view: 'Visualizou um roteiro',
    destination_view: 'Visualizou um destino',
    commercial_click: 'Clicou em um parceiro',
    itinerary_created_planned: 'Criou um roteiro planejado',
    itinerary_created_published: 'Criou um roteiro publicado',
    itinerary_published_transition: 'Marcou um roteiro como realizado'
  }

  return labels[event] || event
}

const AdminUsers = () => {
  const { user } = useUser()
  const { signOut } = useAuth()
  const queryClient = useQueryClient()

  const [search, setSearch] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [status, setStatus] = useState('all')
  const [verified, setVerified] = useState('all')
  const [page, setPage] = useState(1)
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null)
  const [nextStatus, setNextStatus] = useState<AdminUser['status']>('active')
  const [nextVerified, setNextVerified] = useState(false)
  const [nextRole, setNextRole] = useState<AdminUser['role']>('user')
  const [reason, setReason] = useState('')
  const [saving, setSaving] = useState(false)

  const fullName = [user?.firstname, user?.lastname].filter(Boolean).join(' ')

  const { data: adminMe } = useQuery({
    queryKey: ['admin-me'],
    queryFn: async () => {
      const response = await api.get<AdminMe>('/admin/me')
      return response.data
    },
    staleTime: 60 * 1000
  })

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedSearch(search.trim())
      setPage(1)
    }, 450)

    return () => window.clearTimeout(timer)
  }, [search])

  useEffect(() => {
    setPage(1)
  }, [status, verified])

  const queryString = useMemo(() => {
    const params = new URLSearchParams({
      page: String(page),
      limit: '20',
      status,
      verified
    })

    if (debouncedSearch) params.set('search', debouncedSearch)
    return params.toString()
  }, [debouncedSearch, page, status, verified])

  const { data, isLoading, isError } = useQuery({
    queryKey: ['admin-users', queryString],
    queryFn: async () => {
      const response = await api.get<UsersResponse>(
        `/admin/users?${queryString}`
      )
      return response.data
    },
    staleTime: 30 * 1000
  })

  const { data: selectedUser, isLoading: loadingDetails } = useQuery({
    queryKey: ['admin-user-details', selectedUserId],
    queryFn: async () => {
      const response = await api.get<UserDetails>(
        `/admin/users/${selectedUserId}`
      )
      return response.data
    },
    enabled: Boolean(selectedUserId),
    staleTime: 15 * 1000
  })

  useEffect(() => {
    if (!selectedUser) return
    setNextStatus(selectedUser.status)
    setNextVerified(selectedUser.verified)
    setNextRole(
      selectedUser.role ||
        (selectedUser.isMaster
          ? 'master'
          : selectedUser.isAdmin
            ? 'admin'
            : 'user')
    )
    setReason(selectedUser.reason || '')
  }, [selectedUser])

  const saveModeration = async () => {
    if (!selectedUserId || !selectedUser || saving) return

    setSaving(true)

    try {
      await api.patch(`/admin/users/${selectedUserId}/state`, {
        status: nextStatus,
        verified: nextVerified,
        reason
      })

      if (!selectedUser.isMaster && nextRole !== selectedUser.role) {
        await api.patch(`/admin/users/${selectedUserId}/role`, {
          role: nextRole
        })
      }

      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['admin-users'] }),
        queryClient.invalidateQueries({
          queryKey: ['admin-user-details', selectedUserId]
        }),
        queryClient.invalidateQueries({ queryKey: ['admin-dashboard'] }),
        queryClient.invalidateQueries({
          queryKey: ['profileDetails', selectedUserId]
        }),
        queryClient.invalidateQueries({
          queryKey: ['userData', selectedUserId]
        }),
        queryClient.invalidateQueries({ queryKey: ['userData'] }),
        queryClient.invalidateQueries({ queryKey: ['allUsers'] })
      ])

      toast.success('Status do usuário atualizado.', { theme: 'dark' })
    } catch (error: any) {
      toast.error(
        error?.response?.data?.message ||
          'Não foi possível atualizar este usuário.',
        { theme: 'dark' }
      )
    } finally {
      setSaving(false)
    }
  }

  const closeDetails = () => setSelectedUserId(null)

  return (
    <S.Page>
      <S.Sidebar>
        <S.BrandBlock>
          <S.BrandLink to="/">
            <S.Logo src="/images/header-logo.png" alt="RedSterna" />
          </S.BrandLink>
          <S.AdminBadge>
            <FaShieldAlt />
            Admin
          </S.AdminBadge>
        </S.BrandBlock>

        <S.SidebarBody>
          <S.SidebarLabel>Gestão</S.SidebarLabel>
          <S.SidebarNav>
            <S.SidebarNavLink to="/admin">
              <FaChartLine />
              Dashboard
            </S.SidebarNavLink>
            <S.SidebarNavLink to="/admin/usuarios" $active>
              <FaUsers />
              Usuários
            </S.SidebarNavLink>
            <S.SidebarNavLink to="/admin/localidades">
              <FaMapMarkerAlt />
              Localidades
            </S.SidebarNavLink>
            <S.SidebarNavLink to="/admin/roteiros">
              <FaRoute />
              Roteiros
            </S.SidebarNavLink>
            <S.SidebarNavLink to="/admin/anuncios">
              <FaBullhorn />
              Anúncios
            </S.SidebarNavLink>
            <S.SidebarNavLink to="/admin/radar">
              <FaSatelliteDish />
              Radar
            </S.SidebarNavLink>
            <S.SidebarNavLink to="/admin/comissoes">
              <FaDollarSign />
              Comissões
            </S.SidebarNavLink>
          </S.SidebarNav>
        </S.SidebarBody>

        <S.SidebarFooter>
          <S.SidebarLink to="/minha-conta">
            <FaUser />
            Meu perfil
          </S.SidebarLink>
          <S.SidebarLink to="/">
            <FaArrowLeft />
            Voltar ao site
          </S.SidebarLink>
        </S.SidebarFooter>
      </S.Sidebar>

      <S.Main>
        <S.Topbar>
          <div>
            <S.TopbarEyebrow>RedSterna Admin</S.TopbarEyebrow>
            <S.TopbarTitle>Usuários</S.TopbarTitle>
          </div>

          <S.UserArea>
            <S.UserAvatar>
              {user?.profilePicture ? (
                <img
                  src={user.profilePicture}
                  alt={fullName || 'Administrador'}
                />
              ) : (
                <FaUser />
              )}
            </S.UserAvatar>
            <S.UserInfo>
              <strong>{fullName || 'Administrador'}</strong>
              <span>{user?.email}</span>
            </S.UserInfo>
            <S.AdminChip>Admin</S.AdminChip>
            <S.SignOutButton type="button" onClick={signOut} aria-label="Sair">
              <FaSignOutAlt />
            </S.SignOutButton>
          </S.UserArea>
        </S.Topbar>

        <S.Content>
          <S.DashboardHeader>
            <div>
              <h1>Gestão de usuários</h1>
              <p>
                Consulte a comunidade, verifique perfis e modere acessos
                indevidos.
              </p>
            </div>
          </S.DashboardHeader>

          {data && (
            <S.UserSummaryGrid>
              <S.UserSummaryCard>
                <FaUsers />
                <div>
                  <strong>{data.summary.total}</strong>
                  <span>Total de usuários</span>
                </div>
              </S.UserSummaryCard>
              <S.UserSummaryCard $tone="green">
                <FaCheckCircle />
                <div>
                  <strong>{data.summary.active}</strong>
                  <span>Ativos</span>
                </div>
              </S.UserSummaryCard>
              <S.UserSummaryCard $tone="blue">
                <FaUserCheck />
                <div>
                  <strong>{data.summary.verified}</strong>
                  <span>Verificados</span>
                </div>
              </S.UserSummaryCard>
              <S.UserSummaryCard $tone="red">
                <FaBan />
                <div>
                  <strong>
                    {data.summary.suspended + data.summary.banned}
                  </strong>
                  <span>Com acesso restrito</span>
                </div>
              </S.UserSummaryCard>
            </S.UserSummaryGrid>
          )}

          <S.UserToolbar>
            <S.UserSearchInput
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Buscar por nome ou e-mail..."
            />
            <S.UserFilter
              value={status}
              onChange={(event) => setStatus(event.target.value)}
            >
              <option value="all">Todos os status</option>
              <option value="active">Ativos</option>
              <option value="suspended">Suspensos</option>
              <option value="banned">Bloqueados</option>
            </S.UserFilter>
            <S.UserFilter
              value={verified}
              onChange={(event) => setVerified(event.target.value)}
            >
              <option value="all">Verificação: todos</option>
              <option value="yes">Verificados</option>
              <option value="no">Não verificados</option>
            </S.UserFilter>
          </S.UserToolbar>

          {isLoading && <S.StateCard>Carregando usuários...</S.StateCard>}

          {isError && (
            <S.StateCard $error>
              Não foi possível carregar os usuários.
            </S.StateCard>
          )}

          {data && (
            <S.UserTableCard>
              <S.UserTable>
                <thead>
                  <tr>
                    <th>Usuário</th>
                    <th>Função</th>
                    <th>Status</th>
                    <th>Roteiros</th>
                    <th>Seguidores</th>
                    <th>Última atividade</th>
                    <th>Criado em</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {data.items.map((item) => (
                    <tr key={item.id}>
                      <td>
                        <S.UserIdentity>
                          <S.TableAvatar>
                            {item.profilePicture ? (
                              <img src={item.profilePicture} alt="" />
                            ) : (
                              <FaUser />
                            )}
                          </S.TableAvatar>
                          <div>
                            <strong>
                              {item.firstname} {item.lastname}
                            </strong>
                            <span>{item.email}</span>
                          </div>
                        </S.UserIdentity>
                      </td>
                      <td>
                        <S.RoleChip $admin={item.isAdmin}>
                          {item.isMaster
                            ? 'Master'
                            : item.isAdmin
                              ? 'Admin'
                              : item.isPublisher
                                ? 'Publisher'
                                : item.isCommercial
                                  ? 'Comercial'
                                  : 'Usuário'}
                        </S.RoleChip>
                      </td>
                      <td>
                        <S.StatusChip $status={item.status}>
                          {statusLabel(item.status)}
                        </S.StatusChip>
                        {item.verified && (
                          <S.VerifiedMark title="Perfil verificado">
                            <FaCheckCircle />
                          </S.VerifiedMark>
                        )}
                      </td>
                      <td>{item.publishedItineraries}</td>
                      <td>{item.followers}</td>
                      <td>{formatDateTime(item.lastActivity)}</td>
                      <td>{formatDate(item.createdAt)}</td>
                      <td>
                        <S.TableAction
                          type="button"
                          onClick={() => setSelectedUserId(item.id)}
                        >
                          Gerenciar
                        </S.TableAction>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </S.UserTable>

              <S.MobileUserList>
                {data.items.map((item) => (
                  <S.MobileUserCard key={item.id}>
                    <S.MobileUserHeader>
                      <S.UserIdentity>
                        <S.TableAvatar>
                          {item.profilePicture ? (
                            <img src={item.profilePicture} alt="" />
                          ) : (
                            <FaUser />
                          )}
                        </S.TableAvatar>
                        <div>
                          <strong>
                            {item.firstname} {item.lastname}
                          </strong>
                          <span>{item.email}</span>
                        </div>
                      </S.UserIdentity>

                      <S.TableAction
                        type="button"
                        onClick={() => setSelectedUserId(item.id)}
                      >
                        Gerenciar
                      </S.TableAction>
                    </S.MobileUserHeader>

                    <S.MobileUserBadges>
                      <S.RoleChip $admin={item.isAdmin}>
                        {item.isMaster
                          ? 'Master'
                          : item.isAdmin
                            ? 'Admin'
                            : item.isPublisher
                              ? 'Publisher'
                              : item.isCommercial
                                ? 'Comercial'
                                : 'Usuário'}
                      </S.RoleChip>
                      <S.StatusChip $status={item.status}>
                        {statusLabel(item.status)}
                      </S.StatusChip>
                      {item.verified && (
                        <S.MobileVerifiedBadge>
                          <FaCheckCircle />
                          Verificado
                        </S.MobileVerifiedBadge>
                      )}
                    </S.MobileUserBadges>

                    <S.MobileUserMetrics>
                      <div>
                        <strong>{item.publishedItineraries}</strong>
                        <span>Roteiros</span>
                      </div>
                      <div>
                        <strong>{item.followers}</strong>
                        <span>Seguidores</span>
                      </div>
                      <div>
                        <strong>{formatDate(item.createdAt)}</strong>
                        <span>Cadastro</span>
                      </div>
                    </S.MobileUserMetrics>

                    <S.MobileUserActivity>
                      <span>Última atividade</span>
                      <strong>{formatDateTime(item.lastActivity)}</strong>
                    </S.MobileUserActivity>
                  </S.MobileUserCard>
                ))}
              </S.MobileUserList>

              {data.items.length === 0 && (
                <S.EmptyText>
                  Nenhum usuário encontrado com os filtros selecionados.
                </S.EmptyText>
              )}

              <S.Pagination>
                <span>
                  {data.pagination.total} usuários · página{' '}
                  {data.pagination.page} de {data.pagination.pages}
                </span>
                <div>
                  <button
                    type="button"
                    disabled={page <= 1}
                    onClick={() => setPage((current) => current - 1)}
                  >
                    Anterior
                  </button>
                  <button
                    type="button"
                    disabled={page >= data.pagination.pages}
                    onClick={() => setPage((current) => current + 1)}
                  >
                    Próxima
                  </button>
                </div>
              </S.Pagination>
            </S.UserTableCard>
          )}
        </S.Content>
      </S.Main>

      {selectedUserId && (
        <S.AdminModalOverlay onMouseDown={closeDetails}>
          <S.AdminModal
            role="dialog"
            aria-modal="true"
            onMouseDown={(event) => event.stopPropagation()}
          >
            <S.AdminModalHeader>
              <div>
                <S.TopbarEyebrow>Moderação</S.TopbarEyebrow>
                <h2>Detalhes do usuário</h2>
              </div>
              <button type="button" onClick={closeDetails}>
                ×
              </button>
            </S.AdminModalHeader>

            {loadingDetails || !selectedUser ? (
              <S.StateCard>Carregando perfil...</S.StateCard>
            ) : (
              <>
                <S.AdminUserHero>
                  <S.AdminUserAvatar>
                    {selectedUser.profilePicture ? (
                      <img src={selectedUser.profilePicture} alt="" />
                    ) : (
                      <FaUser />
                    )}
                  </S.AdminUserAvatar>
                  <div>
                    <h3>
                      {selectedUser.firstname} {selectedUser.lastname}
                    </h3>
                    <p>{selectedUser.email}</p>
                    <span>
                      Cadastro em {formatDate(selectedUser.createdAt)}
                      {selectedUser.country ? ` · ${selectedUser.country}` : ''}
                    </span>
                  </div>
                  {selectedUser.isMaster ? (
                    <S.AdminChip>Master</S.AdminChip>
                  ) : selectedUser.isAdmin ? (
                    <S.AdminChip>Admin</S.AdminChip>
                  ) : selectedUser.isPublisher ? (
                    <S.AdminChip>Publisher</S.AdminChip>
                  ) : selectedUser.isCommercial ? (
                    <S.AdminChip>Comercial</S.AdminChip>
                  ) : null}
                </S.AdminUserHero>

                <S.AdminUserStats>
                  <div>
                    <strong>{selectedUser.publishedItineraries}</strong>
                    <span>Roteiros publicados</span>
                  </div>
                  <div>
                    <strong>{selectedUser.followers}</strong>
                    <span>Seguidores</span>
                  </div>
                  <div>
                    <strong>{selectedUser.following}</strong>
                    <span>Seguindo</span>
                  </div>
                  <div>
                    <strong>{selectedUser.favorites}</strong>
                    <span>Favoritos</span>
                  </div>
                </S.AdminUserStats>

                <S.ModerationForm>
                  <label>
                    Status de acesso
                    <select
                      value={nextStatus}
                      disabled={selectedUser.isAdmin}
                      onChange={(event) =>
                        setNextStatus(event.target.value as AdminUser['status'])
                      }
                    >
                      <option value="active">Ativo</option>
                      <option value="suspended">Suspenso</option>
                      <option value="banned">Bloqueado</option>
                    </select>
                  </label>

                  <S.CheckLabel>
                    <input
                      type="checkbox"
                      checked={nextVerified}
                      onChange={(event) =>
                        setNextVerified(event.target.checked)
                      }
                    />
                    Marcar perfil como verificado
                  </S.CheckLabel>

                  <label>
                    Função na RedSterna
                    <select
                      value={nextRole}
                      disabled={Boolean(selectedUser.isMaster)}
                      onChange={(event) =>
                        setNextRole(event.target.value as AdminUser['role'])
                      }
                    >
                      {selectedUser.isMaster ? (
                        <option value="master">Master</option>
                      ) : (
                        <>
                          <option value="user">Usuário padrão</option>
                          <option value="publisher">Publisher</option>
                          <option value="commercial">Comercial</option>
                          <option value="admin" disabled={!adminMe?.isMaster}>
                            Administrador
                          </option>
                        </>
                      )}
                    </select>
                  </label>

                  <label>
                    Motivo / observação de moderação
                    <textarea
                      value={reason}
                      onChange={(event) => setReason(event.target.value)}
                      placeholder="Opcional. Registre o motivo de uma suspensão ou bloqueio."
                    />
                  </label>
                </S.ModerationForm>

                {(selectedUser.isAdmin ||
                  selectedUser.isPublisher ||
                  selectedUser.isCommercial) && (
                  <S.AdminNotice>
                    {selectedUser.isMaster
                      ? 'Esta é a conta Master. A função é permanente e não pode ser alterada.'
                      : selectedUser.isAdmin
                        ? adminMe?.isMaster
                          ? 'Esta conta é Administrador. Somente o Master pode retirar ou conceder esta função.'
                          : 'Esta conta é Administrador. Somente o Master pode alterar esta função.'
                        : selectedUser.isPublisher
                          ? 'Publisher tem acesso somente à gestão do Radar.'
                          : 'Comercial tem acesso aos próprios anúncios e comissões.'}
                  </S.AdminNotice>
                )}

                <S.AdminActivity>
                  <h3>Atividade recente</h3>
                  {selectedUser.recentActivity.length ? (
                    selectedUser.recentActivity.slice(0, 8).map((activity) => (
                      <div
                        key={`${activity.event_type}-${activity.created_at}-${activity.entity_id || ''}`}
                      >
                        <strong>{eventLabel(activity.event_type)}</strong>
                        <span>
                          {activity.value_text ||
                            activity.entity_id ||
                            'RedSterna'}
                        </span>
                        <small>{formatDateTime(activity.created_at)}</small>
                      </div>
                    ))
                  ) : (
                    <S.EmptyText>
                      Ainda não há atividade instrumentada para este usuário.
                    </S.EmptyText>
                  )}
                </S.AdminActivity>

                <S.AdminModalActions>
                  <a
                    href={`/usuario/${selectedUser.id}`}
                    target="_blank"
                    rel="noreferrer"
                  >
                    Ver perfil público
                  </a>
                  <button type="button" onClick={closeDetails}>
                    Fechar
                  </button>
                  <button
                    type="button"
                    className="primary"
                    disabled={saving}
                    onClick={saveModeration}
                  >
                    {saving ? 'Salvando...' : 'Salvar alterações'}
                  </button>
                </S.AdminModalActions>
              </>
            )}
          </S.AdminModal>
        </S.AdminModalOverlay>
      )}
    </S.Page>
  )
}

export default AdminUsers
